// src/components/inventory/InventoryTable.js
// FlowSense Enterprise UI — Inventory Asset Table
// Stabilized: Standard Chakra Table with Memoized Rows for high performance.
//
// --- UPDATE (Inventory MOW+ v2) ----------------------------------------------------------
//   • FlowSense AI column (pass `insights` = the AI overview items): risk, days of cover, ML
//     re-order point (teal tick on the stock bar), usage spike, links to assets / vehicles.
//   • Row actions: Move stock (receive / issue / count …) and Linked assets, when handlers given;
//     edit / delete hidden for read-only users (`canManage={false}`).
//   • Live: rows changed by a WebSocket refresh flash teal (`flashIds`).
//   • Memo fix: rows re-rendered only on id / quantity / status, so an edited name, location,
//     threshold or category never showed until reload — the comparator now covers them.
//   • Accessible names on the row buttons include the item name.
// --- END UPDATE ---------------------------------------------------------------------------

import React, { memo, useMemo } from 'react'; // --- UPDATE: + useMemo
import {
  Table, Thead, Tbody, Tr, Th, Td, IconButton, Image, Box, Tooltip, Text,
  Icon, useColorModeValue, Badge, VStack, HStack, Flex, Progress,
  Tag, TagLabel, TagLeftIcon
} from '@chakra-ui/react';
import { EditIcon, DeleteIcon } from '@chakra-ui/icons';
import {
  MdPrint, MdHistory, MdLocationOn, MdWarning, MdTrendingDown, MdCategory, MdInventory
} from 'react-icons/md';
import { MdSwapVert, MdLink, MdTrendingUp, MdAutoAwesome } from 'react-icons/md'; // --- UPDATE
import { FaBoxOpen } from 'react-icons/fa';
import { motion, AnimatePresence } from 'framer-motion';
import { config } from '../../config';
import { RISK_META } from './inventoryAiIntel'; // --- UPDATE

const MotionTr  = motion(Tr);
const MotionBox = motion(Box);

// ─── Stock health bar ─────────────────────────────────────────
const StockBar = memo(({ quantity, threshold, max, aiPoint = null }) => { // --- UPDATE: + aiPoint
  const safeMax = Math.max(max || threshold * 4, quantity, 1, aiPoint || 0); // --- UPDATE: + aiPoint
  const pct     = Math.min((quantity / safeMax) * 100, 100);
  const isLow   = quantity <= threshold;
  const isCrit  = quantity <= Math.floor(threshold * 0.5);
  const color   = isCrit ? 'red' : isLow ? 'orange' : pct < 60 ? 'yellow' : 'green';
  return (
    <Box position="relative" mt={1} w="100%">
      <Progress
        value={pct} size="xs" colorScheme={color}
        borderRadius="full" bg="gray.100"
        sx={{ '& > div': { transition: 'width 0.8s ease' } }}
      />
      <Box
        position="absolute" top="-1px"
        left={`${Math.min((threshold / safeMax) * 100, 100)}%`}
        w="2px" h="8px" bg="purple.500" borderRadius="full"
        transform="translateX(-50%)"
      />
      {/* --- UPDATE: FlowSense ML re-order point */}
      {aiPoint != null && aiPoint > 0 && (
        <Box position="absolute" top="-2px" left={`${Math.min((aiPoint / safeMax) * 100, 100)}%`} w="2px" h="10px" bg="teal.400" borderRadius="full" transform="translateX(-50%)" title={`FlowSense re-order point ${aiPoint}`} />
      )}
    </Box>
  );
});

// ─── Animated status badge ────────────────────────────────────
const StatusBadge = memo(({ status }) => {
  const cfg = {
    active:       { color: 'green',  label: 'Active',       glow: '0 0 8px rgba(56,161,105,0.4)'   },
    discontinued: { color: 'orange', label: 'Discontinued', glow: '0 0 8px rgba(221,107,32,0.3)'   },
    obsolete:     { color: 'red',    label: 'Obsolete',     glow: '0 0 8px rgba(229,62,62,0.3)'    },
  }[status] || { color: 'gray', label: status || 'Active', glow: 'none' };

  return (
    <MotionBox whileHover={{ scale: 1.08 }}>
      <Badge colorScheme={cfg.color} borderRadius="full" textTransform="capitalize" fontSize="xs" fontWeight="700" px={2} py={0.5} boxShadow={cfg.glow}>
        {cfg.label}
      </Badge>
    </MotionBox>
  );
});

// ─── Category chip ────────────────────────────────────────────
const CategoryChip = memo(({ name }) => {
  const palette = ['purple', 'blue', 'teal', 'cyan', 'green', 'orange'];
  const color   = palette[(name?.charCodeAt(0) || 0) % palette.length];
  return (
    <Tag size="sm" colorScheme={color} borderRadius="full" variant="subtle">
      <TagLeftIcon as={MdCategory} boxSize={3} />
      <TagLabel fontSize="xs" fontWeight="600">{name || 'Uncategorized'}</TagLabel>
    </Tag>
  );
});

const ItemImage = memo(({ src, name, fallbackBg }) => (
  <MotionBox whileHover={{ scale: 1.1, rotate: 2 }} transition={{ type: 'spring', stiffness: 300, damping: 16 }} flexShrink={0}>
    {src ? (
      <Image src={src} alt={`${name} QR code`} boxSize="48px" borderRadius="xl" bg="white" p={1} border="1px solid" borderColor="gray.200" objectFit="contain" />
    ) : (
      <Box boxSize="48px" bg={fallbackBg} borderRadius="xl" display="flex" alignItems="center" justifyContent="center" border="1px dashed" borderColor="gray.300">
        <Icon as={FaBoxOpen} color="gray.400" boxSize={5} />
      </Box>
    )}
  </MotionBox>
));

// --- UPDATE (MOW+ v2): FlowSense AI cell
const AiCell = memo(({ ai, muted }) => {
  if (!ai) return <Text fontSize="xs" color={muted}>—</Text>;
  const rm = RISK_META[ai.risk] || RISK_META.ok;
  return (
    <VStack align="start" spacing={0.5}>
      <HStack spacing={1}>
        <Badge colorScheme={rm.scheme} fontSize="2xs" borderRadius="md">{rm.label}</Badge>
        {ai.spike?.is_spike && <Tooltip hasArrow label={`${ai.spike.last_7d} used this week vs ${ai.spike.weekly_avg}/week (z = ${ai.spike.z})`}><Badge colorScheme="purple" fontSize="2xs"><Icon as={MdTrendingUp} /> spike</Badge></Tooltip>}
        {ai.links > 0 && <Tooltip hasArrow label={`Linked to ${ai.links} asset / vehicle target(s)`}><Box as="span"><Icon as={MdLink} color="teal.400" boxSize={3.5} /></Box></Tooltip>}
      </HStack>
      <Tooltip hasArrow label={`Usage ${ai.avg_daily_usage ?? '—'}/day · lead time ${ai.lead_time_days} d · re-order point ${ai.effective_reorder_point ?? '—'} · EOQ ${ai.eoq ?? '—'}${ai.suggested_order_qty ? ` · order ${ai.suggested_order_qty}` : ''}`}>
        <Text fontSize="xs" color={ai.days_of_cover != null && ai.days_of_cover < ai.lead_time_days ? 'red.500' : muted} cursor="help">
          {ai.quantity <= 0 ? 'out of stock' : ai.days_of_cover != null ? `${ai.days_of_cover} d cover` : 'no recent use'}{ai.suggested_order_qty ? ` · order ${ai.suggested_order_qty}` : ''}
        </Text>
      </Tooltip>
    </VStack>
  );
});
// --- END UPDATE

// ─── ROW MEMOIZATION (Performance Engine) ──────────────────────
// This prevents React from redrawing rows unless their specific quantity changes
const InventoryTableRow = memo(({
  item, index, baseUrl, onEdit, onDelete, onPrint, onViewHistory,
  border, hoverBg, nameColor, skuColor, muted, fallbackBg,
  ai = null, flash = false, onMoveStock = null, onLinks = null, canManage = true, showAi = false, chipBg, flashBg, // --- UPDATE
}) => {
  const isLowStock = item.quantity <= item.low_stock_threshold;
  const isCritical = item.quantity <= Math.floor((item.low_stock_threshold || 0) * 0.5);
  const qrSrc      = item.qr_code_url ? `${baseUrl}${item.qr_code_url}` : null;

  return (
    <MotionTr
      initial={{ opacity: 0, y: 10 }}
      animate={flash ? { opacity: 1, y: 0, backgroundColor: ['rgba(56,178,172,0.35)', 'rgba(56,178,172,0)'] } : { opacity: 1, y: 0 }} // --- UPDATE: live flash
      exit={{ opacity: 0, x: -16 }}
      transition={flash ? { duration: 2.2 } : { delay: Math.min(index * 0.045, 0.7), duration: 0.35 }}
      _hover={{ bg: hoverBg }}
      borderLeft={isCritical ? '3px solid' : '3px solid transparent'}
      borderLeftColor={isCritical ? 'red.400' : 'transparent'}
      data-flash={flash ? 'true' : undefined}
    >
      <Td borderColor={border}>
        <HStack spacing={4}>
          <ItemImage src={qrSrc} name={item.name} fallbackBg={fallbackBg} />
          <VStack align="start" spacing={0.5}>
            <HStack spacing={2}>
              <Text fontWeight="700" color={nameColor} fontSize="sm">{item.name}</Text>
              {isLowStock && (
                <MotionBox animate={{ opacity: [1, 0.4, 1] }} transition={{ duration: 1.6, repeat: Infinity }}>
                  <Icon as={isCritical ? MdWarning : MdTrendingDown} color={isCritical ? 'red.500' : 'orange.400'} boxSize={4} />
                </MotionBox>
              )}
            </HStack>
            <Text fontSize="xs" color={skuColor} fontFamily="mono" letterSpacing="wider">
              {item.sku || `ITM-${String(item.id).padStart(4, '0')}`}
            </Text>
          </VStack>
        </HStack>
      </Td>

      <Td borderColor={border}>
        <CategoryChip name={item.category_name} />
      </Td>

      <Td borderColor={border}>
        {item.warehouse_location ? (
          <HStack spacing={1}>
            <Box bg={chipBg || 'gray.100'} p={1} borderRadius="md">{/* --- UPDATE: dark-mode chip */}
              <Icon as={MdLocationOn} color="gray.500" boxSize={3.5} />
            </Box>
            <Text fontSize="sm" fontWeight="500" color={nameColor}>{item.warehouse_location}</Text>
          </HStack>
        ) : (
          <Text fontSize="xs" color={muted} fontStyle="italic">Unassigned</Text>
        )}
      </Td>

      <Td isNumeric borderColor={border}>
        <VStack align="end" spacing={1} minW="80px">
          <HStack spacing={1}>
            <MotionBox animate={isLowStock ? { scale: [1, 1.1, 1] } : {}} transition={{ duration: 1.2, repeat: Infinity }}>
              <Text fontWeight="900" fontSize="xl" color={isCritical ? 'red.500' : isLowStock ? 'orange.500' : 'green.500'} lineHeight="1">
                {item.quantity}
              </Text>
            </MotionBox>
            {item.unit && <Text fontSize="xs" color={muted} fontWeight="500">{item.unit}</Text>}
          </HStack>
          <StockBar quantity={item.quantity} threshold={item.low_stock_threshold || 0} max={item.max_stock} aiPoint={ai?.reorder_point ?? null} />
          <Text fontSize="9px" color={muted} fontWeight="600" letterSpacing="wider">
            MIN {item.low_stock_threshold || 0} {item.max_stock ? ` · MAX ${item.max_stock}` : ''}{ai?.reorder_point != null ? ` · AI ${ai.reorder_point}` : ''}
          </Text>
        </VStack>
      </Td>

      {/* --- UPDATE */}
      {showAi && <Td borderColor={border}><AiCell ai={ai} muted={muted} /></Td>}

      <Td borderColor={border}>
        <StatusBadge status={item.status} />
      </Td>

      <Td borderColor={border} textAlign="right">
        <HStack spacing={1} justify="flex-end">
          <Tooltip label="View Audit History" hasArrow><MotionBox whileHover={{ scale: 1.15 }} whileTap={{ scale: 0.9 }}><IconButton icon={<Icon as={MdHistory} />} size="sm" variant="ghost" colorScheme="gray" borderRadius="full" onClick={() => onViewHistory?.(item)} aria-label={`View history ${item.name}`} /></MotionBox></Tooltip>
          <Tooltip label="Print QR Tag" hasArrow><MotionBox whileHover={{ scale: 1.15 }} whileTap={{ scale: 0.9 }}><IconButton icon={<Icon as={MdPrint} />} size="sm" variant="ghost" colorScheme="teal" borderRadius="full" onClick={() => onPrint?.(item)} aria-label={`Print QR ${item.name}`} /></MotionBox></Tooltip>
          {/* --- UPDATE: stock movement + links */}
          {onMoveStock && canManage && <Tooltip label="Stock movement (receive / issue / count …)" hasArrow><MotionBox whileHover={{ scale: 1.15 }} whileTap={{ scale: 0.9 }}><IconButton icon={<Icon as={MdSwapVert} />} size="sm" variant="ghost" colorScheme="purple" borderRadius="full" onClick={() => onMoveStock(item, ai)} aria-label={`Move stock ${item.name}`} /></MotionBox></Tooltip>}
          {onLinks && <Tooltip label="Linked assets & vehicles" hasArrow><MotionBox whileHover={{ scale: 1.15 }} whileTap={{ scale: 0.9 }}><IconButton icon={<Icon as={MdLink} />} size="sm" variant="ghost" colorScheme="cyan" borderRadius="full" onClick={() => onLinks(item)} aria-label={`Links ${item.name}`} /></MotionBox></Tooltip>}
          {canManage && <Tooltip label="Edit Item" hasArrow><MotionBox whileHover={{ scale: 1.15 }} whileTap={{ scale: 0.9 }}><IconButton icon={<EditIcon />} size="sm" variant="ghost" colorScheme="blue" borderRadius="full" onClick={() => onEdit?.(item)} aria-label={`Edit ${item.name}`} /></MotionBox></Tooltip>}
          {canManage && <Tooltip label="Delete Item" hasArrow><MotionBox whileHover={{ scale: 1.15 }} whileTap={{ scale: 0.9 }}><IconButton icon={<DeleteIcon />} size="sm" variant="ghost" colorScheme="red" borderRadius="full" onClick={() => onDelete?.(item.id)} aria-label={`Delete ${item.name}`} /></MotionBox></Tooltip>}
        </HStack>
      </Td>
    </MotionTr>
  );
}, (prevProps, nextProps) => {
  // Only re-render this specific row if one of these properties changes
  // --- UPDATE: + every displayed field, the AI insight, the live flash and the handlers
  const a = prevProps.item; const b = nextProps.item;
  return a.id === b.id &&
         a.quantity === b.quantity &&
         a.status === b.status &&
         a.name === b.name && a.sku === b.sku && a.warehouse_location === b.warehouse_location &&
         a.low_stock_threshold === b.low_stock_threshold && a.category_name === b.category_name &&
         a.qr_code_url === b.qr_code_url && a.max_stock === b.max_stock && a.unit === b.unit &&
         prevProps.ai === nextProps.ai && prevProps.flash === nextProps.flash && prevProps.canManage === nextProps.canManage &&
         prevProps.showAi === nextProps.showAi && prevProps.onEdit === nextProps.onEdit && prevProps.onMoveStock === nextProps.onMoveStock &&
         prevProps.border === nextProps.border && prevProps.nameColor === nextProps.nameColor;
  // --- END UPDATE
});

// ─────────────────────────────────────────────────────────────
// MAIN TABLE COMPONENT
// ─────────────────────────────────────────────────────────────
const InventoryTable = ({ items = [], onEdit, onDelete, onPrint, onViewHistory,
  insights = null, flashIds = null, onMoveStock = null, onLinks = null, canManage = true, emptyText = null, // --- UPDATE
}) => {
  const bg           = useColorModeValue('white', 'gray.800');
  const border       = useColorModeValue('gray.200', 'gray.700');
  const headerBg     = useColorModeValue('gray.50', 'gray.900');
  const hoverBg      = useColorModeValue('blue.50', 'gray.750');
  const nameColor    = useColorModeValue('gray.800', 'white');
  const muted        = useColorModeValue('gray.500', 'gray.400');
  const skuColor     = useColorModeValue('gray.400', 'gray.500');
  const fallbackBg   = useColorModeValue('gray.100', 'gray.700');
  const chipBg       = useColorModeValue('gray.100', 'whiteAlpha.200'); // --- UPDATE
  const glassBg      = useColorModeValue('rgba(255,255,255,0.92)', 'rgba(26,32,44,0.92)'); // --- UPDATE: hoisted (was inside the object literal)
  const glassStyle   = {
    backdropFilter: 'blur(12px)',
    background: glassBg,
  };

  const baseUrl = config.apiUrl.replace(/\/api\/v1$/, '');

  // --- UPDATE: insight lookup by item id
  const aiById = useMemo(() => {
    if (!insights) return null;
    const m = new Map();
    (Array.isArray(insights) ? insights : Object.values(insights)).forEach((i) => m.set(String(i.id), i));
    return m;
  }, [insights]);
  const showAi = !!aiById;
  // --- END UPDATE

  if (!items || items.length === 0) {
    return (
      <Box bg={bg} borderRadius="2xl" boxShadow="xl" border="1px solid" borderColor={border} overflow="hidden" {...glassStyle}>
        <Box h="3px" bgGradient="linear(to-r, blue.400, teal.400, cyan.400)" />
        <Flex direction="column" align="center" justify="center" p={20} gap={4}>
          <MotionBox initial={{ scale: 0 }} animate={{ scale: 1 }} transition={{ type: 'spring' }}>
            <Box bg="blue.50" p={6} borderRadius="full">
              <Icon as={MdInventory} boxSize={12} color="blue.300" />
            </Box>
          </MotionBox>
          <Text fontWeight="800" fontSize="lg" color={nameColor}>{emptyText ? 'No matching items' : 'No Inventory Items'}</Text>
          <Text color={muted} fontSize="sm" textAlign="center" maxW="xs">
            {emptyText || 'Add your first inventory asset to start tracking stock levels and usage patterns.'}
          </Text>
        </Flex>
      </Box>
    );
  }

  return (
    <Box overflowX="auto" bg={bg} borderRadius="2xl" boxShadow="xl" border="1px solid" borderColor={border} {...glassStyle} position="relative">
      <Box h="3px" bgGradient="linear(to-r, blue.400, teal.400, cyan.400)" />

      <Table variant="simple" aria-label="Inventory items">
        <Thead bg={headerBg}>
          <Tr>
            <Th borderColor={border} py={4}>Item Details</Th>
            <Th borderColor={border}>Category</Th>
            <Th borderColor={border}>Location</Th>
            <Th isNumeric borderColor={border}>Stock Level</Th>
            {showAi && <Th borderColor={border}><HStack spacing={1}><Icon as={MdAutoAwesome} color="teal.400" /><Text>FlowSense AI</Text></HStack></Th>}{/* --- UPDATE */}
            <Th borderColor={border}>Status</Th>
            <Th borderColor={border} textAlign="right">Actions</Th>
          </Tr>
        </Thead>
        <Tbody>
          <AnimatePresence>
            {items.map((item, index) => (
              <InventoryTableRow
                key={item.id}
                item={item}
                index={index}
                baseUrl={baseUrl}
                onEdit={onEdit}
                onDelete={onDelete}
                onPrint={onPrint}
                onViewHistory={onViewHistory}
                border={border}
                hoverBg={hoverBg}
                nameColor={nameColor}
                skuColor={skuColor}
                muted={muted}
                fallbackBg={fallbackBg}
                // --- UPDATE
                ai={aiById ? aiById.get(String(item.id)) || null : null}
                flash={!!flashIds && flashIds.has(String(item.id))}
                onMoveStock={onMoveStock}
                onLinks={onLinks}
                canManage={canManage}
                showAi={showAi}
                chipBg={chipBg}
                // --- END UPDATE
              />
            ))}
          </AnimatePresence>
        </Tbody>
      </Table>

      {/* Table footer */}
      <Flex justify="space-between" align="center" px={5} py={3} borderTop="1px solid" borderColor={border} bg={headerBg}>
        <HStack spacing={3}>
          <Text fontSize="xs" color={muted}>{items.length} items</Text>
          {items.filter(i => i.quantity <= i.low_stock_threshold).length > 0 && (
            <MotionBox animate={{ opacity: [1, 0.5, 1] }} transition={{ duration: 1.8, repeat: Infinity }}>
              <Tag size="sm" colorScheme="orange" borderRadius="full" variant="subtle">
                <TagLeftIcon as={MdWarning} boxSize={3} />
                <TagLabel fontSize="xs" fontWeight="700">
                  {items.filter(i => i.quantity <= i.low_stock_threshold).length} low stock
                </TagLabel>
              </Tag>
            </MotionBox>
          )}
        </HStack>
        <Text fontSize="xs" color={muted}>
          {items.filter(i => i.status === 'active').length} active ·{' '}
          {items.filter(i => i.status !== 'active').length} inactive
        </Text>
      </Flex>
    </Box>
  );
};

export default InventoryTable;
